<template>
  <div class="organization-locations-tab">
    <v-alert
      v-if="locationsError"
      dismissible
      color="error"
      border="left"
      elevation="2"
      colored-border
      icon="priority_high"
    >An error happened while retrieving locations: {{errorDisplay(locationsError)}}</v-alert>
    <v-row>
      <v-col cols="12">
        <v-card
          :disabled="organizationLocationsLoading || locationsLoading"
          :loading="organizationLocationsLoading || locationsLoading"
        >
          <v-card-title>Overview</v-card-title>
          <v-card-text>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate>
                  <span>Not Imported Locations ({{notImportedLocations.length}}/{{locations.length}})</span>
                  <template v-slot:actions>
                    <v-icon
                      :color="notImportedLocations.length === 0 ? 'success':'error'"
                    >{{notImportedLocations.length === 0 ? 'mdi-check':'mdi-alert-circle'}}</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    class="title-1"
                  >The following locations are in Kitchen Manager system but not referenced in IAM:</div>
                  <v-list dense shaped max-height="300px" style="overflow-y: auto;" two-line>
                    <v-list-item-group>
                      <v-list-item v-for="loc in notImportedLocations" :key="loc.uuid" dense>
                        <v-list-item-content>
                          <v-list-item-title>UUID : {{loc.uuid}}</v-list-item-title>
                          <v-list-item-subtitle>Label: {{loc.label}}</v-list-item-subtitle>
                          <v-list-item-subtitle>Country: {{loc.location_country.country_code}}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn color="primary" small rounded @click="importLocation(loc.uuid)">
                            <v-icon left small>publish</v-icon>
                            <span>Import</span>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate>
                  <span>Unknown Locations ({{unknownLocations.length}})</span>
                  <template v-slot:actions>
                    <v-icon
                      :color="unknownLocations.length ? 'error': 'success'"
                    >{{unknownLocations.length ? 'mdi-alert-circle': 'mdi-check'}}</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    v-if="unknownLocations.length"
                    class="title-1"
                  >The following locations are in IAM system but not referenced in Kitchen Manager:</div>
                  <div v-else class="title-1">No unknown location.</div>
                  <v-list dense shaped max-height="300px" style="overflow-y: auto;">
                    <v-list-item-group>
                      <v-list-item v-for="loc in unknownLocations" :key="loc.uuid" dense>
                        <v-list-item-content>
                          <v-list-item-title>UUID : {{loc.uuid}}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn color="error" small rounded @click="removeLocation(loc.uuid)">
                            <v-icon left small>delete</v-icon>
                            <span>Remove</span>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card
          :disabled="organizationLocationsLoading || locationsLoading"
          :loading="organizationLocationsLoading || locationsLoading"
        >
          <v-card-title>Available Locations</v-card-title>
          <v-treeview
            selected-color="primary"
            :items="nestedLocations ? nestedLocations: []"
            item-children="locations"
            item-text="label"
            item-key="uuid"
            hoverable
            rounded
          >
            <template v-slot:prepend="{ item }">
              <v-icon
                v-if="item.locations ? item.locations.length : false"
                :color="organizationLocationsUuids.includes(item.uuid) ? 'success':'error'"
              >flag</v-icon>
              <v-icon
                v-else
                :color="organizationLocationsUuids.includes(item.uuid) ? 'success':'error'"
              >restaurant</v-icon>
            </template>
            <template v-slot:append="{ item }">
              <v-btn
                :disabled="organizationLocationsUuids.includes(item.uuid)"
                v-if="!organizationLocationsUuids.includes(item.uuid)"
                color="primary"
                dark
                small
                rounded
                @click="importLocation(item.uuid)"
              >
                <v-icon left small>publish</v-icon>
                <span>Import</span>
              </v-btn>

              <v-btn
                class="ml-2"
                color="error"
                v-if="organizationLocationsUuids.includes(item.uuid)"
                :disabled="!organizationLocationsUuids.includes(item.uuid)"
                small
                rounded
                @click="removeLocation(item.uuid)"
              >
                <v-icon left small>delete</v-icon>
                <span>Remove</span>
              </v-btn>
              <v-icon
                class="ml-2"
                :color="isComplete(item) ? 'success':'error'"
              >{{isComplete(item) ? 'turned_in':'turned_in_not'}}</v-icon>
            </template>
          </v-treeview>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import mixin from "@/mixins";
export default {
  mixins: [mixin],
  props: {
    organizationLocations: { type: Array, required: true },
    locations: { type: Array, required: true },
    nestedLocations: { type: Array, required: true }
  },
  data() {
    return {
      alert: false
    };
  },
  computed: {
    ...mapState({
      locationsError: state => state.locations.error,
      organizationLocationsLoading: state =>
        state.organizations.fetchAllLocations.pending,
      locationsLoading: state => state.locations.fetchAll.pending
    }),
    importedLocations() {
      return this.locations.filter(location =>
        this.organizationLocationsUuids.includes(location.uuid)
      );
    },
    notImportedLocations() {
      return this.locations.filter(
        location => !this.organizationLocationsUuids.includes(location.uuid)
      );
    },
    unknownLocations() {
      return this.organizationLocations.filter(
        location => !this.locations.map(loc => loc.uuid).includes(location.uuid)
      );
    },
    organizationLocationsUuids() {
      return this.organizationLocations.map(location => location.uuid);
    }
  },
  methods: {
    isComplete(item) {
      for (var loc in item.locations) {
        if (
          !this.organizationLocationsUuids.includes(item.locations[loc].uuid)
        ) {
          return false;
        }
      }
      if (!this.organizationLocationsUuids.includes(item.uuid)) {
        return false;
      }
      return true;
    },
    importLocation(locationUuid) {
      this.$emit("importLocation", locationUuid);
    },
    removeLocation(locationUuid) {
      this.$emit("removeLocation", locationUuid);
    }
  }
};
</script>

<style>
</style>